/**
 * 天地图工具类
 * 封装天地图API的常用操作
 */

// 天地图API密钥
const TIANDITU_API_KEY = 'e69697d4bc2ebe482db6a41b102ccaab';

/**
 * 等待天地图API加载完成
 * @returns {Promise} Promise对象
 */
export function waitForTiandituAPI() {
  return new Promise((resolve) => {
    // 优先检查全局标志
    if (window.tiandituAPILoaded && typeof T !== 'undefined' && T.Map && T.LngLat) {
      resolve();
      return;
    }
    
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      if ((window.tiandituAPILoaded || typeof T !== 'undefined') && T && T.Map && T.LngLat) {
        clearInterval(checkInterval);
        resolve();
      } else if (checkCount > 100) {
        clearInterval(checkInterval);
        console.error('天地图API加载超时，请检查网络连接和API密钥配置');
        console.log('T对象:', typeof T, T);
        resolve();
      }
    }, 100);
  });
}

/**
 * 检查天地图API是否加载
 * @returns {boolean} 是否已加载
 */
export function isTiandituAPILoaded() {
  return typeof T !== 'undefined' && T.Map && T.LngLat;
}

/**
 * 创建天地图实例
 * @param {string} containerId - 地图容器ID
 * @param {Object} options - 地图配置选项
 * @param {number} options.centerLng - 中心点经度
 * @param {number} options.centerLat - 中心点纬度
 * @param {number} options.zoom - 缩放级别，默认10
 * @param {string} options.tk - API密钥，默认使用全局密钥
 * @returns {Object|null} 地图实例
 */
export function createTiandituMap(containerId, options = {}) {
  if (!isTiandituAPILoaded()) {
    console.error('天地图API未加载');
    return null;
  }
  
  const mapContainer = document.getElementById(containerId);
  if (!mapContainer) {
    console.error(`地图容器不存在: ${containerId}`);
    return null;
  }
  
  const {
    centerLng = 113.937122,
    centerLat = 22.542874,
    zoom = 10,
    tk = TIANDITU_API_KEY
  } = options;
  
  try {
    // 确保 tk 是字符串类型
    const tkString = String(tk || TIANDITU_API_KEY);
    console.log('创建地图，tk类型:', typeof tkString, 'tk值:', tkString);
    
    // 天地图4.0版本 - 创建地图实例
    // 注意：天地图4.0在创建地图时传入tk，地图会自动加载默认图层
    // 天地图4.0默认启用拖拽功能，不需要额外调用 enableDrag
    const map = new T.Map(containerId, {
      center: new T.LngLat(centerLng, centerLat),
      zoom: zoom,
      tk: tkString
    });
    
    // 天地图4.0可能不需要手动添加图层，地图创建时会自动加载
    // 如果需要添加图层，可以延迟添加
    setTimeout(() => {
      try {
        // 检查是否已有图层
        let hasLayer = false;
        if (typeof map.getLayers === 'function') {
          const layers = map.getLayers();
          hasLayer = layers && layers.length > 0;
        }
        
        // 如果没有图层，尝试添加
        if (!hasLayer && T.TileLayer) {
          if (T.TileLayer.Vector) {
            try {
              const vectorLayer = new T.TileLayer.Vector({
                tk: tkString
              });
              if (typeof map.addLayer === 'function') {
                map.addLayer(vectorLayer);
              }
            } catch (layerError) {
              console.warn('添加矢量图层失败:', layerError);
            }
          } else {
            try {
              const normalLayer = new T.TileLayer({
                tk: tkString
              });
              if (typeof map.addLayer === 'function') {
                map.addLayer(normalLayer);
              } else if (typeof map.addOverlay === 'function') {
                map.addOverlay(normalLayer);
              }
            } catch (layerError) {
              console.warn('添加普通图层失败:', layerError);
            }
          }
        }
      } catch (e) {
        console.warn('检查或添加图层时出错:', e);
      }
    }, 100);
    
    return map;
  } catch (error) {
    console.error('创建天地图失败:', error);
    console.error('错误详情:', error.message, error.stack);
    return null;
  }
}

/**
 * 设置地图事件（启用滚轮缩放、拖拽等）
 * @param {Object} map - 地图实例
 */
export function setMapEvent(map) {
  if (!map) return;
  
  // 启用拖拽功能
  if (typeof map.enableDragging === 'function') {
    try {
      map.enableDragging();
    } catch (e) {
      console.warn('启用拖拽失败:', e);
    }
  } else if (typeof map.enableDrag === 'function') {
    try {
      map.enableDrag();
    } catch (e) {
      console.warn('启用拖拽失败:', e);
    }
  } else if (typeof map.setDragging === 'function') {
    try {
      map.setDragging(true);
    } catch (e) {
      console.warn('设置拖拽失败:', e);
    }
  }
  
  // 启用滚轮缩放
  if (typeof map.enableScrollWheelZoom === 'function') {
    try {
      map.enableScrollWheelZoom();
    } catch (e) {
      console.warn('启用滚轮缩放失败:', e);
    }
  } else if (typeof map.setScrollWheelZoom === 'function') {
    try {
      map.setScrollWheelZoom(true);
    } catch (e) {
      console.warn('设置滚轮缩放失败:', e);
    }
  }
  
  // 启用键盘控制
  if (typeof map.enableKeyboard === 'function') {
    try {
      map.enableKeyboard();
    } catch (e) {
      console.warn('启用键盘控制失败:', e);
    }
  } else if (typeof map.setKeyboard === 'function') {
    try {
      map.setKeyboard(true);
    } catch (e) {
      console.warn('设置键盘控制失败:', e);
    }
  }
  
  // 启用双击缩放
  if (typeof map.enableDoubleClickZoom === 'function') {
    try {
      map.enableDoubleClickZoom();
    } catch (e) {
      console.warn('启用双击缩放失败:', e);
    }
  } else if (typeof map.setDoubleClickZoom === 'function') {
    try {
      map.setDoubleClickZoom(true);
    } catch (e) {
      console.warn('设置双击缩放失败:', e);
    }
  }
}

/**
 * 添加地图控件（比例尺、缩放、鹰眼）
 * @param {Object} map - 地图实例
 */
export function addMapControl(map) {
  if (!map || !isTiandituAPILoaded()) return;
  
  try {
    // 天地图比例尺控件
    const scaleControl = new T.Control.Scale();
    map.addControl(scaleControl);
    
    // 天地图缩放控件
    const zoomControl = new T.Control.Zoom();
    map.addControl(zoomControl);
    
    // 天地图鹰眼控件
    const overviewControl = new T.Control.OverviewMap({
      isOpen: false
    });
    map.addControl(overviewControl);
  } catch (error) {
    console.warn('添加地图控件失败:', error);
  }
}

/**
 * 创建标记点
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @param {Object} options - 标记选项
 * @param {string} options.iconUrl - 图标URL
 * @param {number} options.iconSizeWidth - 图标宽度，默认26
 * @param {number} options.iconSizeHeight - 图标高度，默认36
 * @param {number} options.iconAnchorX - 图标锚点X，默认13
 * @param {number} options.iconAnchorY - 图标锚点Y，默认36
 * @returns {Object|null} 标记点实例
 */
export function createMarker(lng, lat, options = {}) {
  if (!isTiandituAPILoaded()) {
    console.error('天地图API未加载');
    return null;
  }
  
  const {
    iconUrl = require('@/assets/images/mark.png'),
    iconSizeWidth = 26,
    iconSizeHeight = 36,
    iconAnchorX = 13,
    iconAnchorY = 36
  } = options;
  
  try {
    const point = new T.LngLat(lng, lat);
    const markerIcon = new T.Icon({
      iconUrl: iconUrl,
      iconSize: new T.Point(iconSizeWidth, iconSizeHeight),
      iconAnchor: new T.Point(iconAnchorX, iconAnchorY)
    });
    const marker = new T.Marker(point, { icon: markerIcon });
    return marker;
  } catch (error) {
    console.error('创建标记点失败:', error);
    return null;
  }
}

/**
 * 添加标记点到地图
 * @param {Object} map - 地图实例
 * @param {Object} marker - 标记点实例
 * @returns {boolean} 是否添加成功
 */
export function addMarkerToMap(map, marker) {
  if (!map || !marker) return false;
  
  try {
    if (typeof map.addOverlay === 'function') {
      map.addOverlay(marker);
      return true;
    } else if (typeof map.addLayer === 'function') {
      map.addLayer(marker);
      return true;
    } else if (typeof map.add === 'function') {
      map.add(marker);
      return true;
    } else {
      console.warn('无法添加标注到地图，未找到合适的方法');
      return false;
    }
  } catch (error) {
    console.error('添加标记点到地图失败:', error);
    return false;
  }
}

/**
 * 移除标记点
 * @param {Object} map - 地图实例
 * @param {Object} marker - 标记点实例
 * @returns {boolean} 是否移除成功
 */
export function removeMarkerFromMap(map, marker) {
  if (!map || !marker) return false;
  
  try {
    if (typeof map.removeOverlay === 'function') {
      map.removeOverlay(marker);
      return true;
    } else if (typeof map.removeLayer === 'function') {
      map.removeLayer(marker);
      return true;
    } else if (typeof map.remove === 'function') {
      map.remove(marker);
      return true;
    } else {
      console.warn('无法移除标注，未找到合适的方法');
      return false;
    }
  } catch (error) {
    console.error('移除标记点失败:', error);
    return false;
  }
}

/**
 * 移除所有Marker类型的覆盖物
 * @param {Object} map - 地图实例
 */
export function clearAllMarkers(map) {
  if (!map || !isTiandituAPILoaded()) return;
  
  try {
    let allOverlay = [];
    if (typeof map.getOverlays === 'function') {
      allOverlay = map.getOverlays();
    } else if (typeof map.getLayers === 'function') {
      allOverlay = map.getLayers();
    }
    
    // 移除所有Marker类型的覆盖物
    const markersToRemove = [];
    for (let i = 0; i < allOverlay.length; i++) {
      if (allOverlay[i] instanceof T.Marker) {
        markersToRemove.push(allOverlay[i]);
      }
    }
    
    // 先收集再移除，避免遍历时修改数组导致的问题
    markersToRemove.forEach(marker => {
      removeMarkerFromMap(map, marker);
    });
    
    console.log(`清除了 ${markersToRemove.length} 个marker`);
  } catch (error) {
    console.warn('清除所有标记点失败:', error);
  }
}

/**
 * 添加单个标记点并清除之前的标记点
 * @param {number} lat - 纬度
 * @param {number} lng - 经度
 * @param {Object} map - 地图实例
 * @param {string} typeImg - 图标图片URL（可选）
 * @returns {Object|null} 标记点实例
 */
export function addMapOverlayl(lat, lng, map, typeImg) {
  if (!isTiandituAPILoaded()) {
    console.error('天地图API未加载或缺少必要的类');
    return null;
  }
  
  if (!map) {
    console.error('地图对象不存在');
    return null;
  }
  
  // 清除之前的标记点
  clearAllMarkers(map);
  
  // 创建新标记点
  const iconUrl = (typeImg && typeImg != "null") 
    ? 'http://slwl-parking.oss-cn-shenzhen.aliyuncs.com/iotExper/' + typeImg 
    : require('@/assets/images/mark.png');
  
  const marker = createMarker(lng, lat, {
    iconUrl: iconUrl,
    iconSizeWidth: 26,
    iconSizeHeight: 36,
    iconAnchorX: 13,
    iconAnchorY: 36
  });
  
  if (marker) {
    addMarkerToMap(map, marker);
  }
  
  return marker;
}

/**
 * 更新标记点位置
 * @param {Object} marker - 标记点实例
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @returns {boolean} 是否更新成功
 */
export function updateMarkerPosition(marker, lng, lat) {
  if (!marker || !isTiandituAPILoaded()) {
    return false;
  }
  
  try {
    const newPoint = new T.LngLat(lng, lat);
    // 天地图4.0可能使用 setPosition 或 setLngLat 方法
    if (typeof marker.setPosition === 'function') {
      marker.setPosition(newPoint);
      return true;
    } else if (typeof marker.setLngLat === 'function') {
      marker.setLngLat(newPoint);
      return true;
    } else if (typeof marker.setPoint === 'function') {
      marker.setPoint(newPoint);
      return true;
    } else {
      console.warn('无法更新标记点位置，未找到合适的方法');
      return false;
    }
  } catch (error) {
    console.error('更新标记点位置失败:', error);
    return false;
  }
}

/**
 * 地理编码 - 根据地址获取坐标
 * @param {string} address - 地址
 * @param {Function} callback - 回调函数，参数为 {lng, lat, point}
 */
export function geocodeAddress(address, callback) {
  if (!isTiandituAPILoaded() || !T.Geocoder) {
    console.error('天地图API未加载或Geocoder不可用');
    return;
  }
  
  try {
    const geocoder = new T.Geocoder();
    geocoder.getPoint(address, (point) => {
      if (callback) {
        if (point) {
          // 处理不同的返回格式
          let lng, lat;
          if (point.location) {
            lng = point.location.lon || point.location.lng;
            lat = point.location.lat;
          } else {
            lng = point.lng || point.lon;
            lat = point.lat;
          }
          callback({
            lng: lng,
            lat: lat,
            point: point,
            address: address
          });
        } else {
          callback({
            lng: null,
            lat: null,
            point: null,
            address: address
          });
        }
      }
    });
  } catch (error) {
    console.error('地理编码失败:', error);
  }
}

/**
 * 逆地理编码 - 根据坐标获取地址
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @param {Function} callback - 回调函数，参数为 {address, result, lng, lat}
 */
export function reverseGeocode(lng, lat, callback) {
  if (!isTiandituAPILoaded() || !T.Geocoder) {
    console.error('天地图API未加载或Geocoder不可用');
    // 即使API未加载，也返回经纬度信息
    if (callback) {
      callback({
        address: null,
        lng: lng,
        lat: lat,
        result: null
      });
    }
    return;
  }
  
  try {
    const geocoder = new T.Geocoder();
    geocoder.getLocation(new T.LngLat(lng, lat), (result) => {
      if (callback) {
        let address = null;
        
        // 天地图4.0返回的数据结构：result.formatted_address 或 result.addressComponent.address
        if (result) {
          // 优先使用 formatted_address（完整地址）
          if (result.formatted_address) {
            address = result.formatted_address;
          } 
          // 如果没有 formatted_address，尝试使用 addressComponent.address
          else if (result.addressComponent && result.addressComponent.address) {
            address = result.addressComponent.address;
          }
          // 兼容旧版本：直接使用 address 字段
          else if (result.address) {
            address = result.address;
          }
        }
        
        callback({
          address: address,
          lng: lng,
          lat: lat,
          result: result
        });
      }
    });
  } catch (error) {
    console.error('逆地理编码失败:', error);
    // 即使编码失败，也返回经纬度信息
    if (callback) {
      callback({
        address: null,
        lng: lng,
        lat: lat,
        result: null
      });
    }
  }
}

/**
 * 设置地图中心点和缩放级别
 * @param {Object} map - 地图实例
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @param {number} zoom - 缩放级别（可选）
 */
export function setMapCenter(map, lng, lat, zoom) {
  if (!map || !isTiandituAPILoaded()) return;
  
  try {
    if (zoom !== undefined) {
      map.centerAndZoom(new T.LngLat(lng, lat), zoom);
    } else {
      map.panTo(new T.LngLat(lng, lat));
    }
  } catch (error) {
    console.error('设置地图中心点失败:', error);
  }
}

export default {
  waitForTiandituAPI,
  isTiandituAPILoaded,
  createTiandituMap,
  setMapEvent,
  addMapControl,
  createMarker,
  addMarkerToMap,
  removeMarkerFromMap,
  clearAllMarkers,
  addMapOverlayl,
  updateMarkerPosition,
  geocodeAddress,
  reverseGeocode,
  setMapCenter,
  TIANDITU_API_KEY
};
